import type { Question } from '../types';

export const MIN_SELECTIONS_METADATA_KEY = 'min_selections';
export const MAX_SELECTIONS_METADATA_KEY = 'max_selections';

export type MultipleSelectLimits = {
  minSelections?: number;
  maxSelections?: number;
};

export type ParsedMultipleSelectLimit = {
  value?: number;
  error?: string;
};

const WHOLE_NUMBER_ERROR = 'Must be a whole number greater than or equal to 1.';

export const parseMultipleSelectLimitValue = (rawValue: unknown): ParsedMultipleSelectLimit => {
  if (rawValue === null || typeof rawValue === 'undefined') {
    return {};
  }

  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (!trimmed) {
      return {};
    }
    if (!/^\d+$/.test(trimmed)) {
      return { error: WHOLE_NUMBER_ERROR };
    }
    const parsed = Number(trimmed);
    if (!Number.isInteger(parsed) || parsed < 1) {
      return { error: WHOLE_NUMBER_ERROR };
    }
    return { value: parsed };
  }

  if (typeof rawValue === 'number') {
    if (!Number.isInteger(rawValue) || rawValue < 1) {
      return { error: WHOLE_NUMBER_ERROR };
    }
    return { value: rawValue };
  }

  return { error: WHOLE_NUMBER_ERROR };
};

export const getMultipleSelectLimits = (
  metadata: Question['metadata'] | Record<string, unknown> | null | undefined,
): MultipleSelectLimits => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    return {};
  }

  const minParsed = parseMultipleSelectLimitValue(metadata[MIN_SELECTIONS_METADATA_KEY]);
  const maxParsed = parseMultipleSelectLimitValue(metadata[MAX_SELECTIONS_METADATA_KEY]);

  if (minParsed.error || maxParsed.error) {
    return {};
  }

  if (
    typeof minParsed.value === 'number' &&
    typeof maxParsed.value === 'number' &&
    minParsed.value > maxParsed.value
  ) {
    return {};
  }

  return {
    minSelections: minParsed.value,
    maxSelections: maxParsed.value,
  };
};

export const isMultipleSelectSelectionCountValid = (
  selectionCount: number,
  metadata: Question['metadata'] | Record<string, unknown> | null | undefined,
): boolean => {
  const { minSelections, maxSelections } = getMultipleSelectLimits(metadata);

  if (typeof minSelections === 'number' && selectionCount < minSelections) {
    return false;
  }

  if (typeof maxSelections === 'number' && selectionCount > maxSelections) {
    return false;
  }

  return true;
};

export const getMultipleSelectRequirementText = (
  metadata: Question['metadata'] | Record<string, unknown> | null | undefined,
): string | null => {
  const { minSelections, maxSelections } = getMultipleSelectLimits(metadata);

  if (typeof minSelections === 'number' && typeof maxSelections === 'number') {
    if (minSelections === maxSelections) {
      return `Select ${minSelections} option${minSelections === 1 ? '' : 's'}.`;
    }
    return `Select between ${minSelections} and ${maxSelections} options.`;
  }

  if (typeof minSelections === 'number') {
    return `Select at least ${minSelections} option${minSelections === 1 ? '' : 's'}.`;
  }

  if (typeof maxSelections === 'number') {
    return `Select up to ${maxSelections} option${maxSelections === 1 ? '' : 's'}.`;
  }

  return null;
};
